import { useState, type JSX } from "react";
import { toast } from "sonner";
import { useLiveQuery } from "dexie-react-hooks";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/ui/sheet.js";
import { db } from "../../sync/db.js";
import { sync } from "../../sync/SyncService.js";
import { friendlyError } from "../../shared/http.js";
import { useMe } from "../auth/useMe.js";
import { duplicateWeek } from "./duplicateWeek.js";

export interface DuplicateWeekSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The week currently being viewed — the duplicate's destination. */
  targetWeekStart: string;
}

function nowIso(): string {
  return new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
}

/**
 * Lets the user duplicate a prior, populated week into the week they're currently
 * viewing. Composition is entirely the pure `duplicateWeek` helper; this component's job
 * is picking a source week and POSTing the (narrowed-down) result via the idempotent-create
 * path (`sync.createMealPlanOptimistic`) — which is a safe no-op even if the target
 * somehow already has a row (server returns the existing plan untouched on conflict). We
 * ALSO guard in the UI so a populated target never even shows the picker: an accidental
 * tap on "duplicate" should never *read* as data loss, even though the server can't
 * actually lose anything here.
 */
export function DuplicateWeekSheet({
  open,
  onOpenChange,
  targetWeekStart,
}: DuplicateWeekSheetProps): JSX.Element {
  const { me } = useMe();
  const [busy, setBusy] = useState(false);

  // Live so the guard below always reflects current Dexie state (e.g. if the target week
  // gets created from another tab/device while this sheet happens to be open).
  const targetPlan = useLiveQuery(() => db.mealPlans.get(targetWeekStart), [targetWeekStart]);
  const priorWeeks = useLiveQuery(async () => {
    const all = await db.mealPlans.toArray();
    return all
      .filter((p) => p.weekStartDate < targetWeekStart)
      .sort((a, b) => (a.weekStartDate < b.weekStartDate ? 1 : -1)); // most recent first
  }, [targetWeekStart]);

  async function handleDuplicate(sourceWeekStart: string): Promise<void> {
    setBusy(true);
    try {
      // Re-read both sides fresh at the moment of action — the guard must hold even if the
      // live queries above haven't re-rendered yet. (The server-side idempotent-create
      // POST is the real backstop against ever overwriting a populated week; this is
      // defense-in-depth so the UI never even attempts it.)
      const [source, existingTarget] = await Promise.all([
        db.mealPlans.get(sourceWeekStart),
        db.mealPlans.get(targetWeekStart),
      ]);
      if (!source) throw new Error("Source week not found");
      if (existingTarget) {
        onOpenChange(false);
        return;
      }
      const composed = duplicateWeek(source, targetWeekStart, me?.user.userId ?? "", nowIso());
      // CreateMealPlanRequestSchema is `.strict()` server-side — narrow the composed full
      // plan body down to just the wire fields before POSTing.
      await sync.createMealPlanOptimistic({
        weekStart: composed.weekStartDate,
        days: composed.days,
        tasks: composed.tasks,
        ...(composed.cookingFor !== undefined ? { cookingFor: composed.cookingFor } : {}),
      });
      onOpenChange(false);
    } catch (e) {
      toast.error(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  const alreadyPopulated = targetPlan !== undefined;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Duplicate a prior week</SheetTitle>
          <SheetDescription>
            {alreadyPopulated
              ? "This week already has a plan — duplicate is only offered for empty weeks."
              : "Copy a prior week's dishes into this week. Tasks aren't copied."}
          </SheetDescription>
        </SheetHeader>

        {!alreadyPopulated && (
          <div className="flex flex-col gap-1 px-4 pb-4">
            {!priorWeeks || priorWeeks.length === 0 ? (
              <p className="text-sm text-zinc-500">No prior weeks to duplicate yet.</p>
            ) : (
              priorWeeks.map((w) => (
                <button
                  key={w.weekStartDate}
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    void handleDuplicate(w.weekStartDate);
                  }}
                  className="rounded-md px-3 py-2 text-left text-sm text-zinc-200 hover:bg-zinc-800"
                >
                  Week of {w.weekStartDate}
                </button>
              ))
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
